import React from 'react'
import { useState, useMemo } from 'react';
import './Report.css'
import Header from '../../components/common/header/Header'
import lcn from "../../images/loc-n.svg";
import ReportData from '../../data/Report.json';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import VehicleCard from '../../components/vehicleCard/VehicleCard';
import { getRandomDate, getRelativeDate } from '../../utils/date.utils';


function getTypeClass(type) {
    switch (type) {
        case 'CRITICAL': return 'type-critical';
        case 'MINOR': return 'type-minor';
        default: return '';
    }
}
function getStatusClass(status) {
    switch (status) {
        case 'RESOLVED': return 'status-resolved';
        case 'OVERDUE': return 'status-overdue';
        case 'PENDING': return 'status-pending';
        default: return '';
    }
}

function getStatushClass(statush) {
    switch (statush) {
        case 'Moving': return 'status-moving';
        case 'Idle': return 'status-idle';
        case 'In Maintenance': return 'status-maintenance';
        case 'Offline': return 'status-offline';
        case 'Parked': return 'status-parked';
        case 'Alert': return 'status-alert';
        default: return '';
    }
}

function getConditionClass(condition) {
    switch (condition) {
        case 'GOOD': return 'condition-good';
        case 'NOT GOOD': return 'condition-notgood';
        case 'AVERAGE': return 'condition-average';

        default: return '';
    }
}

const getBase64FromUrl = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};


const Report = () => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    // Generate dynamic timestamps for vehicles and their issues
    const processedReportData = useMemo(() => ({
        ...ReportData,
        vehicles: ReportData.vehicles.map(vehicle => ({
            ...vehicle,
            timestamp: getRandomDate(),
            next_maintenance_date: getRelativeDate(30),
            telemetry: {
                ...vehicle.telemetry,
                next_maintenance: getRelativeDate(30)
            },
            issues: (vehicle.issues || []).map((issue, idx) => ({
                ...issue,
                predicted_date: getRelativeDate(idx + 1), // Predicted in future
                reported_date: getRandomDate(5), // Reported recently
                resolved_on: issue.status === 'RESOLVED' ? getRandomDate(1) : issue.resolved_on
            }))
        }))
    }), []);

    // Filter vehicles based on search query
    const filteredVehicles = processedReportData.vehicles.filter(vehicle => {
        const searchLower = searchQuery.toLowerCase();
        return (
            vehicle.name.toLowerCase().includes(searchLower) ||
            vehicle.device_id.toLowerCase().includes(searchLower) ||
            vehicle.license_plate.toLowerCase().includes(searchLower)
        );
    });

    // Use selected vehicle from filtered list
    const selectedVehicle = filteredVehicles[selectedIndex] || filteredVehicles[0];

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        // Reset selected index when searching
        if (e.target.value === '') {
            setSelectedIndex(0);
        }
    };

    const generatePDF = async () => {
        const doc = new jsPDF();

        // Fetch and add the logo
        const logoBase64 = await getBase64FromUrl('/images/pm-logo.png');
        doc.addImage(logoBase64, 'PNG', 12, 1, 50, 16); // (x, y, width, height)

        // Add title (move down to avoid overlap)
        doc.setFontSize(20);
        doc.text('Vehicle Maintenance Report', 14, 30);

        // Add vehicle details (move down by 15)
        doc.setFontSize(12);
        doc.text(`Vehicle Name: ${selectedVehicle.name}`, 14, 45);
        doc.text(`Device ID: ${selectedVehicle.device_id}`, 14, 55);
        doc.text(`License Plate: ${selectedVehicle.license_plate}`, 14, 65);
        doc.text(`Status: ${selectedVehicle.status}`, 14, 75);
        doc.text(`Health Status: ${selectedVehicle.health_status}`, 14, 85);
        doc.text(`Location: ${selectedVehicle.location}`, 14, 95);

        if (selectedVehicle.imageUrl) {
            try {
                const imageBase64 = await getBase64FromUrl(selectedVehicle.imageUrl);
                // Note: SVG support in jsPDF is limited — prefer PNG/JPEG if possible.
                doc.addImage(imageBase64, 'PNG', 150, 50, 40, 30); // (x, y, width, height)
            } catch (error) {
                console.error('Error loading vehicle image:', error);
            }
        }
        // Add top-bar telemetry content in two lines
        if (selectedVehicle.telemetry) {
            const telemetry = selectedVehicle.telemetry;
            const telemetryFields = [
                { label: 'Model', value: telemetry.model },
                { label: 'Fuel Rate', value: telemetry.fuel_rate },
                { label: 'Engine Speed', value: telemetry.engine_speed },
                { label: 'Engine Pressure', value: telemetry.engine_pressure },
                { label: 'Vibration', value: telemetry.vibration },
                { label: 'Temperature', value: telemetry.temperature },
                { label: 'Engine Oil Pressure', value: telemetry.oil_pressure },
                { label: 'Odometer', value: telemetry.odometer },
                { label: 'Mileage', value: telemetry.mileage },
                { label: 'Engine % Torque', value: telemetry.torque },
                { label: 'Engine % Load', value: telemetry.engine_load },
                { label: 'Engine Hours', value: telemetry.engine_hours },
                { label: 'Next Maintenance', value: new Date(telemetry.next_maintenance).toLocaleDateString() },
            ];
            // Split into two rows
            const half = Math.ceil(telemetryFields.length / 2);
            const rows = [
                telemetryFields.slice(0, half),
                telemetryFields.slice(half)
            ];
            const startY = 105;
            const cellHeight = 12;
            const cellWidth = 180 / rows[0].length; // Use the longer row for width

            doc.setFontSize(10);
            rows.forEach((row, rowIndex) => {
                row.forEach((field, i) => {
                    const x = 14 + i * cellWidth;
                    const y = startY + rowIndex * cellHeight;
                    // Draw cell background
                    doc.setFillColor(24, 31, 42);
                    doc.rect(x, y, cellWidth, cellHeight, 'F');
                    // Draw value
                    doc.setTextColor(255, 255, 255);
                    doc.text(String(field.value), x + 2, y + 5);
                    // Draw label
                    doc.setFontSize(7);
                    doc.setTextColor(191, 201, 218);
                    doc.text(field.label, x + 2, y + 10);
                    doc.setFontSize(10);
                });
            });
        }
        // Move issues table startY down to avoid overlap with telemetry (2 rows)
        const tableStartY = selectedVehicle.telemetry ? 105 + (2 * 12) + 5 : 105; // 2 rows * cellHeight + margin
        const tableColumn = ["Issue", "Type", "Description", "Predicted Date", "Action", "Reported", "Maintenance Hours", "Resolved On", "Status"];
        const tableRows = selectedVehicle.issues.map(issue => [
            issue.issue,
            issue.type,
            issue.description,
            issue.predicted_date ? new Date(issue.predicted_date).toLocaleDateString() : 'N/A',
            issue.action,
            issue.reported_date ? new Date(issue.reported_date).toLocaleDateString() : 'N/A',
            issue.maintenance_hours,
            issue.resolved_on ? new Date(issue.resolved_on).toLocaleDateString() : 'N/A',
            issue.status
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: tableStartY,
            theme: 'grid',
            styles: {
                fontSize: 8,
                cellPadding: 2,
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontSize: 8,
                fontStyle: 'bold',
            },
            didParseCell: function (data) {
                // Type column (index 1)
                if (data.section === 'body' && data.column.index === 1) {
                    if (data.cell.raw === 'CRITICAL') {
                        data.cell.styles.fillColor = [213, 0, 0]; // red
                        data.cell.styles.textColor = 255;
                    } else if (data.cell.raw === 'MINOR') {
                        data.cell.styles.fillColor = [255, 107, 0]; // orange
                        data.cell.styles.textColor = 255;
                    }
                }
                // Status column (index 8)
                if (data.section === 'body' && data.column.index === 8) {
                    if (data.cell.raw === 'RESOLVED') {
                        data.cell.styles.fillColor = [93, 244, 108]; // green
                        data.cell.styles.textColor = 0;
                    } else if (data.cell.raw === 'OVERDUE') {
                        data.cell.styles.fillColor = [213, 0, 0]; // red
                        data.cell.styles.textColor = 255;
                    } else if (data.cell.raw === 'PENDING') {
                        data.cell.styles.fillColor = [255, 107, 0]; // orange
                        data.cell.styles.textColor = 255;
                    }
                }
            },
        });

        // Save the PDF
        doc.save(`${selectedVehicle.name}_maintenance_report.pdf`);
    };

    const sidebarRef = React.useRef(null);
    const cardRefs = React.useRef([]);

    // Add auto-scroll to selected card
    React.useEffect(() => {
        if (cardRefs.current[selectedIndex]) {
            cardRefs.current[selectedIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [selectedIndex, filteredVehicles]);

    return (
        <div className='main-div'>
            <Header />
            <div className="report-dashboard">
                <aside className="sidebar" ref={sidebarRef}>
                    <div className="sidebar-header">
                        <span>Vehicles</span>
                        <input
                            className="vehicle-search"
                            type="text"
                            placeholder=" "
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                    </div>
                    <footer className="overflow-auto" style={{ maxHeight: 'calc(100vh - 165px)' }}>
                        {filteredVehicles.map((v, i) => (
                            <div
                                key={v.id}
                                ref={el => { cardRefs.current[i] = el; }}
                                style={{ marginBottom: '10px', marginLeft: '5px', marginRight: '5px', marginTop: '5px' }}
                                onClick={() => {
                                    setSelectedIndex(i);
                                }}
                            >
                                <VehicleCard
                                    vehicleId={v.id}
                                    vehicleBasicInfo={{
                                        vehicle: v.name,
                                        deviceId: v.device_id,
                                        licensePlate: v.license_plate,
                                        status: v.status,
                                        location: v.location,
                                        timestamp: v.timestamp || '',
                                        imageUrl: v.imageUrl || '',
                                        metrics: v.metrics || { speed: '', fuelRate: '', mileage: '', temperature: '', pressure: '', vibration: '' }
                                    }}
                                    expandMetrics={selectedIndex === i}
                                    hideMoreInfoButton={true}
                                    onClickVehicleCard={() => { }}
                                />
                            </div>
                        ))}
                    </footer>
                </aside>

                <main className="main-content">

                    <div className="vehicle-header">
                        <div className="vehicle-title">
                            <span className="vehicle-name" title={selectedVehicle.name} >{selectedVehicle.name}</span> |
                            <span className="device-id" title={selectedVehicle.device_id}> DEVICE ID - {selectedVehicle.device_id}</span> |
                            <span className="license" title={selectedVehicle.license_plate}> License Plate No. - {selectedVehicle.license_plate}</span>
                        </div>
                        <div className="vehicle-status">
                            <span className={`issue-type ${getStatushClass(selectedVehicle.status)}`}>
                                <span className="status-dot"></span>
                                {selectedVehicle.status}
                            </span>
                            <span className="health-status">
                                Health Status <span className={`issue-type ${getConditionClass(selectedVehicle.health_status)}`}>{selectedVehicle.health_status}</span>
                            </span>
                            <span className="location" title={selectedVehicle.location}>
                                <img src={lcn} alt="" /> {selectedVehicle.location}
                            </span>
                        </div>
                        <button className="download-report" onClick={generatePDF}>Download Report</button>
                    </div>

                    <div className='top-bar' style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(13, 1fr)',
                        background: '#181f2a',
                        borderRadius: 6,
                        margin: '10px 0',
                        border: '1px solid #fff',
                        width: '100%',
                        overflow: 'hidden'
                    }}>
                        {selectedVehicle.telemetry && (
                            <>
                                <div style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #232b3e' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{selectedVehicle.telemetry.model}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Model</div>
                                </div>
                                <div style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #232b3e' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{selectedVehicle.telemetry.fuel_rate}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Fuel Rate</div>
                                </div>
                                <div style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #232b3e' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{selectedVehicle.telemetry.engine_speed}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Engine Speed</div>
                                </div>
                                <div style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #232b3e' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{selectedVehicle.telemetry.engine_pressure}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Engine Pressure</div>
                                </div>
                                <div style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #232b3e' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{selectedVehicle.telemetry.vibration}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Vibration</div>
                                </div>
                                <div style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #232b3e' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{selectedVehicle.telemetry.temperature}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Temperature</div>
                                </div>
                                <div style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #232b3e' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{selectedVehicle.telemetry.oil_pressure}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Engine Oil Pressure</div>
                                </div>
                                <div style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #232b3e' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{selectedVehicle.telemetry.odometer}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Odometer</div>
                                </div>
                                <div style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #232b3e' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{selectedVehicle.telemetry.mileage}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Mileage</div>
                                </div>
                                <div style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #232b3e' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{selectedVehicle.telemetry.torque}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Engine % Torque</div>
                                </div>
                                <div style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #232b3e' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{selectedVehicle.telemetry.engine_load}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Engine % Load</div>
                                </div>
                                <div style={{ padding: '10px 12px', textAlign: 'center', borderRight: '1px solid #232b3e' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{selectedVehicle.telemetry.engine_hours}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Engine Hours</div>
                                </div>
                                <div style={{ padding: '10px 12px', textAlign: 'center' }}>
                                    <div style={{ fontWeight: 600, color: '#fff', fontSize: 16 }}>{new Date(selectedVehicle.telemetry.next_maintenance).toLocaleDateString()}</div>
                                    <div style={{ color: '#bfc9da', fontSize: 13 }}>Next Maintenance</div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="issues-table-container">
                        <table className="issues-table">
                            <thead>
                                <tr>
                                    <th>ISSUE</th>
                                    <th>ISSUE TYPE</th>
                                    <th>DESCRIPTION</th>
                                    <th>PREDICTED DATE</th>
                                    <th>ACTION</th>
                                    <th>REPORTED</th>
                                    <th>MAINTENANCE HOURS</th>
                                    <th>RESOLVED ON</th>
                                    <th>STATUS</th>
                                </tr>
                            </thead>
                            <tbody style={{ backgroundColor: "#060606" }}>
                                {selectedVehicle.issues.map((row, i) => (
                                    <tr key={i}>
                                        <td className="td">{row.issue}</td>
                                        <td className='td'><span className={`issue-type ${getTypeClass(row.type)}`}>{row.type}</span></td>
                                        <td className="td">{row.description}</td>
                                        <td className="td">{new Date(row.predicted_date).toLocaleDateString()}</td>
                                        <td className="td">{row.action}</td>
                                        <td className="td">{new Date(row.reported_date).toLocaleDateString()}</td>
                                        <td className="td">{row.maintenance_hours}</td>
                                        <td className="td">{row.resolved_on ? new Date(row.resolved_on).toLocaleDateString() : 'N/A'}</td>
                                        <td className='td'><span className={`status-badge ${getStatusClass(row.status)}`}>{row.status}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Report;